import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import { filter, Subject, takeUntil } from 'rxjs';

import {
    DEFAULT_LANDING_PAGE_ID,
    LANDING_PAGE_OPTIONS,
    LandingPageOption,
    LandingPagePreference,
} from '../../../../../services/landing-page-preference.service';
import { Organization } from '../../models/organization.class';
import { LoadUiPreferences, SaveUiPreferences } from '../../store/actions';
import { GetAssociatedLocations } from '../../store/actions/locations.actions';
import { getLandingPagePreference$, getUiPreferences$, getUiPreferencesLoaded$ } from '../../store/selectors';
import { getAssociatedOrganizations$ } from '../../store/selectors/locations.selector';
import { getCurrentOrganizationId$ } from '../../store/selectors/session.selector';

interface TenantRow {
    id: string;
    name: string;
    /** Full ancestry path, e.g. Org380QA | Location12 | SubLocation12 */
    path: string;
    /** Path split into segments so the leaf can be emphasised over its ancestors */
    pathSegments: string[];
    orgLoginId: string;
    depth: number;
}

@Component({
    selector: 'app-landing-page-preference',
    templateUrl: './landing-page-preference.component.html',
    styleUrls: ['./landing-page-preference.component.scss'],
    standalone: false,
})
export class LandingPagePreferenceComponent implements OnInit, OnDestroy {
    /** Lets the host dialog guard against closing with unsaved changes */
    @Output() dirtyChange = new EventEmitter<boolean>();

    public pageOptions: LandingPageOption[] = LANDING_PAGE_OPTIONS;
    public defaultPageId = DEFAULT_LANDING_PAGE_ID;

    /** ALL_TENANTS state — empty string means "no selection" */
    public allTenantsPage = '';

    /** PER_TENANT state: a landing page per tenant. */
    public tenantPages: Record<string, string> = {};

    public tenants: TenantRow[] = [];
    public filteredTenants: TenantRow[] = [];
    public tenantSearch = '';
    public currentOrganizationId: string;
    public hasSavedPreference = false;
    public isDirty = false;
    public justSaved = false;

    private savedSnapshot = '';
    private touched = false;
    private unSubscribe = new Subject<void>();
    private justSavedTimeout: any;

    constructor(private store$: Store) {}

    public get configuredTenantsCount(): number {
        return Object.keys(this.tenantPages).length;
    }

    public selectAllTenantsPage = (pageId: string) => {
        // Toggle: clicking the already-selected chip deselects it
        this.allTenantsPage = this.allTenantsPage === pageId ? '' : pageId;
        this.touched = true;
        this.checkDirty();
    };

    /** Assign a page to a tenant, or clear it (pageId undefined) */
    public setTenantPage = (tenantId: string, pageId: string | undefined) => {
        if (pageId) {
            this.tenantPages = { ...this.tenantPages, [tenantId]: pageId };
        } else {
            const pages = { ...this.tenantPages };
            delete pages[tenantId];
            this.tenantPages = pages;
        }
        this.touched = true;
        this.checkDirty();
    };

    /** Toggle: click selected chip again to deselect */
    public toggleTenantPage = (tenantId: string, pageId: string) => {
        const current = this.tenantPages[tenantId];
        this.setTenantPage(tenantId, current === pageId ? undefined : pageId);
    };

    public clearAllTenantPages = () => {
        this.tenantPages = {};
        this.touched = true;
        this.checkDirty();
    };

    public filterTenants = (search: string) => {
        this.tenantSearch = search || '';
        const text = this.tenantSearch.trim().toLowerCase();
        this.filteredTenants = !text
            ? this.tenants
            : this.tenants.filter(tenant => tenant.path?.toLowerCase().includes(text) || tenant.orgLoginId?.toLowerCase().includes(text));
    };

    public getPageOption = (pageId: string): LandingPageOption => this.pageOptions.find(option => option.id === pageId);

    public trackTenant = (_index: number, tenant: TenantRow): string => tenant.id;

    public canSave = (): boolean => this.isDirty;

    public save = () => {
        if (!this.canSave()) {
            return;
        }
        const preference = this.buildPreference();
        // Read current raw preferences from store and merge landing page key
        let currentRaw: Record<string, any> = {};
        this.store$
            .select(getUiPreferences$)
            .subscribe(raw => {
                currentRaw = { ...(raw ?? {}) };
            })
            .unsubscribe();
        const updated = { ...currentRaw, landingPage: preference };
        this.store$.dispatch(SaveUiPreferences({ preferences: updated, successMessage: 'Landing page preference saved successfully' }));
        this.hasSavedPreference = true;
        this.savedSnapshot = this.buildSnapshot();
        this.touched = false;
        this.checkDirty();
        this.flashSavedState();
    };

    public discardChanges = () => {
        this.loadFromStore();
        this.touched = false;
        this.checkDirty();
    };

    public resetToDefault = () => {
        // Remove only the landingPage key — preserve all other preferences (closure config, defaultTenantId, etc.)
        let currentRaw: Record<string, any> = {};
        this.store$
            .select(getUiPreferences$)
            .subscribe(raw => {
                currentRaw = { ...(raw ?? {}) };
            })
            .unsubscribe();
        delete currentRaw.landingPage;
        this.store$.dispatch(SaveUiPreferences({ preferences: currentRaw, successMessage: 'Landing page preference reset to default' }));

        this.allTenantsPage = '';
        this.tenantPages = {};
        this.hasSavedPreference = false;
        this.savedSnapshot = this.buildSnapshot();
        this.touched = false;
        this.checkDirty();
    };

    private buildPreference = (): LandingPagePreference => ({
        allTenantsPage: this.allTenantsPage || undefined,
        tenantPages: { ...this.tenantPages },
    });

    private buildSnapshot = (): string => {
        const all = this.allTenantsPage || '';
        const entries = Object.entries(this.tenantPages)
            .filter(([, page]) => !!page)
            .sort(([a], [b]) => a.localeCompare(b));
        if (!all && entries.length === 0) {
            return 'DEFAULT';
        }
        return `ALL:${all}|PER:${JSON.stringify(entries)}`;
    };

    private checkDirty = () => {
        this.isDirty = this.touched && this.buildSnapshot() !== this.savedSnapshot;
        this.dirtyChange.emit(this.isDirty);
    };

    private flashSavedState = () => {
        this.justSaved = true;
        clearTimeout(this.justSavedTimeout);
        this.justSavedTimeout = setTimeout(() => (this.justSaved = false), 2400);
    };

    /** Hydrate local component state from the NgRx store */
    private loadFromStore = () => {
        this.store$
            .select(getLandingPagePreference$)
            .subscribe(preference => {
                this.hasSavedPreference = !!(preference?.allTenantsPage || Object.keys(preference?.tenantPages || {}).length);
                this.allTenantsPage = preference?.allTenantsPage || '';
                this.tenantPages = { ...(preference?.tenantPages || {}) };
                this.savedSnapshot = this.buildSnapshot();
            })
            .unsubscribe();
    };

    private flattenOrganizations = (organizations: Organization[], ancestry: string[], rows: TenantRow[]) => {
        organizations?.forEach(organization => {
            const name = organization.name || organization.nickName;
            const pathSegments = [...ancestry, name];
            rows.push({
                id: organization.id,
                name,
                path: pathSegments.join(' | '),
                pathSegments,
                orgLoginId: organization.orgLoginId,
                depth: ancestry.length,
            });
            organization.childNodes?.length > 0 && this.flattenOrganizations(organization.childNodes, pathSegments, rows);
        });
    };

    private initiateSubscriptions = () => {
        this.store$.dispatch(GetAssociatedLocations());

        // Load preferences from backend if not already loaded
        this.store$
            .select(getUiPreferencesLoaded$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(loaded => {
                if (!loaded) {
                    this.store$.dispatch(LoadUiPreferences());
                }
            });

        // React to preference changes from the store (e.g. after backend load)
        this.store$
            .select(getLandingPagePreference$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(preference => {
                if (!this.touched) {
                    this.hasSavedPreference = !!(preference?.allTenantsPage || Object.keys(preference?.tenantPages || {}).length);
                    this.allTenantsPage = preference?.allTenantsPage || '';
                    this.tenantPages = { ...(preference?.tenantPages || {}) };
                    this.savedSnapshot = this.buildSnapshot();
                    this.checkDirty();
                }
            });

        this.store$
            .select(getAssociatedOrganizations$)
            .pipe(takeUntil(this.unSubscribe))
            .subscribe(organizations => {
                if (!organizations?.length) {
                    return;
                }
                const rows: TenantRow[] = [];
                this.flattenOrganizations(organizations, [], rows);
                this.tenants = rows;
                this.filterTenants(this.tenantSearch);
            });

        this.store$
            .select(getCurrentOrganizationId$)
            .pipe(
                takeUntil(this.unSubscribe),
                filter(organizationId => organizationId !== undefined)
            )
            .subscribe(organizationId => {
                this.currentOrganizationId = organizationId;
            });
    };

    ngOnInit(): void {
        this.initiateSubscriptions();
    }

    ngOnDestroy(): void {
        clearTimeout(this.justSavedTimeout);
        this.unSubscribe.next();
        this.unSubscribe.complete();
    }
}
